import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useInviteAuth } from './Invite/InviteAuthContext';
import { useHistory, useLocation } from 'react-router-dom';
import API_BASE_URL from '../config';
import './Marketplace.css';
import RoleSelectionModal from '../components/RoleSelectionModal';
import InviteAuthModal from '../components/InviteAuthModal';
import PlanningProgressSidebar from '../components/PlanningProgressSidebar';
import CheckAvailabilityModal from '../components/CheckAvailabilityModal';
import AgencyEnquiryModal from '../components/AgencyEnquiryModal';
import { ShoppingBag, Heart, Sparkles, Search, Plus, CalendarPlus, Check, Clock, MapPin, Navigation, Filter, ChevronRight, ChevronLeft, X, Building, Users, Mail, Gem, Send, PartyPopper, HeartHandshake, Shirt, Camera, Flower2, Utensils, Music, Star } from 'lucide-react';

// Dynamic data will be fetched from backend

interface MarketVendor {
    _id: string;
    name: string;
    category: string;
    primaryContact: string;
    priceRange: string;
    rating: number;
    city: string;
    description: string;
    images?: string[];
    instagramUrl?: string;
    facebookUrl?: string;
    isVerified?: boolean;
    vendorType?: 'product' | 'service';
    isPreferred?: boolean;
}

interface Product {
    id: string;
    vendorId: string;
    vendorName: string;
    name: string;
    price: number;
    originalPrice?: number;
    image: string;
    category: string;
    rating: number;
    reviews: number;
    vendorCity: string;
    vendorType?: 'product' | 'service';
}

// Categories will be fetched from backend

// Mock products removed to use real backend data

const BANNER_SLIDES = [
    {
        tag: 'Bridal Season 2026',
        title: 'The Grand Wedding Sale',
        description: 'Up to 40% off on designer bridal wear & premium decor packages.',
        bgImage: 'https://images.unsplash.com/photo-1543604130-9dbcd9d750c1?auto=format&fit=crop&q=80&w=2000',
        overlayColor: 'linear-gradient(to right, rgba(255, 64, 129, 0.6), rgba(33, 150, 243, 0.6))',
        btnText: 'Shop Now',
        action: 'products'
    },
    {
        tag: 'Elite Concierge',
        title: 'Bespoke Wedding Agencies',
        description: 'Collaborate with top-tier planners to craft your dream luxury wedding.',
        bgImage: 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&q=80&w=2000',
        overlayColor: 'linear-gradient(to right, rgba(62, 31, 36, 0.75), rgba(212, 175, 55, 0.6))',
        btnText: 'Consult an Expert',
        action: 'agency'
    },
    {
        tag: 'Royal Collections',
        title: 'Curated Designer Portfolios',
        description: 'Discover award-winning catering services, photography portfolios and decorators.',
        bgImage: 'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&q=80&w=2000',
        overlayColor: 'linear-gradient(to right, rgba(33, 150, 243, 0.6), rgba(124, 58, 237, 0.6))',
        btnText: 'Explore Categories',
        action: 'categories'
    },
    {
        tag: 'Cultural Experience',
        title: 'Be a Guest at an Indian Wedding',
        description: 'Are you a traveler wanting to experience the colors, dances, and rich traditions of traditional Indian celebrations? Join us today!',
        bgImage: 'https://images.unsplash.com/photo-1607190074257-dd4b7af0309f?auto=format&fit=crop&q=80&w=2000',
        overlayColor: 'linear-gradient(to right, rgba(168, 85, 247, 0.6), rgba(236, 72, 153, 0.6))',
        btnText: 'Experience Culture',
        action: 'culturalExperience'
    }
];

const fetchWithTimeout = (url: string, options: RequestInit = {}, timeout = 20000): Promise<Response> => {
    return Promise.race([
        fetch(url, options),
        new Promise<Response>((_, reject) =>
            setTimeout(() => reject(new Error(`Request to ${url} timed out`)), timeout)
        )
    ]);
};

const Marketplace: React.FC = () => {
    const { user, accessToken } = useAuth();
    const { inviteUser } = useInviteAuth();
    const isInviteUser = !!inviteUser;
    const history = useHistory();
    const location = useLocation();
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
    const [vendors, setVendors] = useState<MarketVendor[]>([]);
    const [products, setProducts] = useState<Product[]>([]);
    const [cartCount, setCartCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<'products' | 'stores' | 'rentals'>('stores');

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tab = params.get('tab');
        if (tab === 'products' || tab === 'stores' || tab === 'rentals') {
            setActiveTab(tab as any);
        }
    }, [location.search]);
    const [rentals, setRentals] = useState<any[]>([]);
    const [selectedRentalForDetails, setSelectedRentalForDetails] = useState<any | null>(null);
    const [rentalStartDate, setRentalStartDate] = useState('');
    const [rentalEndDate, setRentalEndDate] = useState('');
    const [selectedSize, setSelectedSize] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [sortBy, setSortBy] = useState('recommended');
    const [maxPrice, setMaxPrice] = useState<number>(2000000);
    const [shortlistedItems, setShortlistedItems] = useState<string[]>([]);
    const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
    const [addedId, setAddedId] = useState<string | null>(null);
    const [isAvailModalOpen, setIsAvailModalOpen] = useState(false);
    const [selectedVendorForAvail, setSelectedVendorForAvail] = useState<{id: string, name: string}[]>([]);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
    const [isAgencyEnquiryModalOpen, setIsAgencyEnquiryModalOpen] = useState(false);
    const [activeSlide, setActiveSlide] = useState(0);
    
    const [cities, setCities] = useState<string[]>(['All India']);
    const [categories, setCategories] = useState<{id: string, name: string, icon: React.ReactNode}[]>([]);
    
    // City selection & detection state
    const [selectedCity, setSelectedCity] = useState('All India');
    const [isDetecting, setIsDetecting] = useState(false);
    const [showCityDropdown, setShowCityDropdown] = useState(false);

    // Scroll-to-hide header on mobile
    const [showHeader, setShowHeader] = useState(true);
    const lastScrollY = React.useRef(0);

    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY;
            
            if (window.innerWidth <= 768) {
                if (currentScrollY < 10) {
                    setShowHeader(true);
                    lastScrollY.current = currentScrollY;
                    return;
                }
                const diff = Math.abs(currentScrollY - lastScrollY.current);
                if (diff > 15) {
                    if (currentScrollY > lastScrollY.current && currentScrollY > 80) {
                        setShowHeader(false);
                    } else {
                        setShowHeader(true);
                    }
                    lastScrollY.current = currentScrollY;
                }
            } else {
                setShowHeader(true);
            }
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const categoryIcons: {[key: string]: React.ReactNode} = {
        'Clothing': <Shirt size={18} />,
        'Photography': <Camera size={18} />,
        'Decoration': <Flower2 size={18} />,
        'Venues': <Building size={18} />,
        'Catering': <Utensils size={18} />,
        'Beautician': <Sparkles size={18} />,
        'Invitation': <Mail size={18} />,
        'Entertainment': <Music size={18} />
    };


    useEffect(() => {
        const saved = localStorage.getItem('shadigram_shortlist');
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                if (Array.isArray(parsed)) {
                    setShortlistedItems(parsed);
                }
            } catch (e) {
                console.error('Failed to parse shortlist from localStorage on mount:', e);
            }
        }
    }, []);

    useEffect(() => {
        const timer = setInterval(() => {
            setActiveSlide(prev => (prev + 1) % BANNER_SLIDES.length);
        }, 6000);
        return () => clearInterval(timer);
    }, []);

    const toggleShortlist = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        const vendorId = id.startsWith('prod_') ? id.split('_')[1] : id;
        const isCurrentlyShortlisted = shortlistedItems.includes(id);
        const updated = isCurrentlyShortlisted
            ? shortlistedItems.filter(item => item !== id)
            : [...shortlistedItems, id];

        setShortlistedItems(updated);
        localStorage.setItem('shadigram_shortlist', JSON.stringify(updated));

        // If logged in, sync with backend
        if (accessToken) {
            try {
                if (isCurrentlyShortlisted) {
                    // Remove from backend
                    await fetch(`${API_BASE_URL}/api/market-vendors/shortlist/${vendorId}`, {
                        method: 'DELETE',
                        headers: { 'Authorization': `Bearer ${accessToken}` }
                    });
                } else {
                    // Add to backend
                    await fetch(`${API_BASE_URL}/api/market-vendors/${vendorId}/shortlist`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${accessToken}` }
                    });
                }
            } catch (error) {
                console.error('Failed to sync shortlist with backend:', error);
            }
        }

        // Dispatch event so Navbar can update live
        window.dispatchEvent(new Event('storage'));
    };
    
    const togglePreference = async (id: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!accessToken || user?.role !== 'agency') return;
        
        try {
            const res = await fetch(`${API_BASE_URL}/api/market-vendors/${id}/preference`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${accessToken}` }
            });
            if (res.ok) {
                setVendors(prev => prev.map(v => 
                    v._id === id ? { ...v, isPreferred: !v.isPreferred } : v
                ));
            }
        } catch (err) {
            console.error('Failed to toggle preference:', err);
        }
    };

    const handleCheckAvailability = (id: string, name: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedVendorForAvail([{ id, name }]);
        setIsAvailModalOpen(true);
    };

    const submitEnquiry = async (data: any) => {
        if (!accessToken) {
            history.push('/login');
            return;
        }
        
        const vendorId = selectedVendorForAvail[0].id;
        const res = await fetch(`${API_BASE_URL}/api/market-vendors/${vendorId}/enquiry`, {
            method: 'POST',
            headers: { 
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });
        
        if (!res.ok) throw new Error('Failed to send enquiry');
    };

    useEffect(() => {
        fetchData();
    }, [accessToken]);

    const handleDetectLocation = () => {
        if (!navigator.geolocation) {
            alert('Geolocation is not supported by your browser');
            return;
        }

        setIsDetecting(true);
        navigator.geolocation.getCurrentPosition(async (position) => {
            try {
                const { latitude, longitude } = position.coords;
                // Reverse geocoding using Nominatim (free)
                const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`);
                const data = await res.json();
                
                // Try to find city in address object
                const detectedCity = data.address.city || data.address.town || data.address.village || data.address.state_district || '';
                console.log('[Marketplace] Detected location:', detectedCity);

                // Match against our city list
                const matchedCity = cities.find(c => 
                    detectedCity.toLowerCase().includes(c.toLowerCase()) || 
                    c.toLowerCase().includes(detectedCity.toLowerCase())
                );

                if (matchedCity) {
                    setSelectedCity(matchedCity);
                } else {
                    // If no match, maybe it's a new city or just keep current
                    console.log('[Marketplace] No matching city found in our list for:', detectedCity);
                    // For now, if we detect something but it's not in our list, we could add it or just set it
                    if (detectedCity) setSelectedCity(detectedCity);
                }
            } catch (err) {
                console.error('Failed to detect city:', err);
            } finally {
                setIsDetecting(false);
                setShowCityDropdown(false);
            }
        }, (error) => {
            console.error('Geolocation error:', error);
            setIsDetecting(false);
            alert('Could not detect your location. Please select manually.');
        });
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch Cities
            const cityRes = await fetchWithTimeout(`${API_BASE_URL}/api/public/cities`, {}, 20000);
            if (cityRes.ok) {
                const cityData = await cityRes.json();
                setCities(['All India', ...(cityData.cities || []).map((c: any) => c.name)]);
            }

            // Fetch Categories
            const catRes = await fetchWithTimeout(`${API_BASE_URL}/api/market-vendors/categories`, {}, 20000);
            if (catRes.ok) {
                const catData = await catRes.json();
                const formattedCats = [
                    { id: 'all', name: 'All', icon: <ShoppingBag size={18} /> },
                    ...(catData.categories || []).map((c: any) => ({
                        id: c.name,
                        name: c.name,
                        icon: categoryIcons[c.name] || <Sparkles size={18} />
                    }))
                ];
                setCategories(formattedCats);
            }


            const fetchOpts = accessToken
                ? { headers: { 'Authorization': `Bearer ${accessToken}` } }
                : {};

            const url = new URL(`${API_BASE_URL}/api/market-vendors`);
            url.searchParams.append('ignoreSettings', 'true');
            if (selectedCategory !== 'all') url.searchParams.append('category', selectedCategory);
            if (selectedCity !== 'All India') url.searchParams.append('city', selectedCity);
            if (searchQuery) url.searchParams.append('search', searchQuery);

            const res = await fetchWithTimeout(url.toString(), fetchOpts, 20000);

            if (res.ok) {
                const vendorData = await res.json();
                let initialVendors = vendorData.vendors || [];
                console.log(`[Marketplace] Fetched ${initialVendors.length} vendors. Role: ${user?.role || 'guest'}`);

                if (accessToken && user?.role === 'agency') {
                    try {
                        const prefRes = await fetchWithTimeout(`${API_BASE_URL}/api/market-vendors/preferences`, {
                            headers: { 'Authorization': `Bearer ${accessToken}` }
                        }, 20000);
                        if (prefRes.ok) {
                            const prefData = await prefRes.json();
                            const prefIds = new Set((prefData.preferences || []).map((p: any) => p.marketVendorId));
                            initialVendors = initialVendors.map((v: any) => ({
                                ...v,
                                isPreferred: prefIds.has(v._id)
                            }));
                        }
                    } catch (e) {
                        console.error('Failed to fetch agency preferences:', e);
                    }
                }

                setVendors(initialVendors);
                
                // Map Vendors with vendorType 'product' to the products tab
                const productList = initialVendors
                    .filter((v: any) => v.vendorType === 'product')
                    .map((v: any) => ({
                        id: v._id,
                        vendorId: v._id,
                        vendorName: v.name,
                        name: v.name,
                        price: parseInt(v.priceRange?.match(/\d+/)?.[0] || '1000') * 1000,
                        originalPrice: (parseInt(v.priceRange?.match(/\d+/)?.[0] || '1000') * 1000) * 1.2,
                        image: v.images?.[0] || 'https://images.unsplash.com/photo-1549416878-b9ca95e1bbbb?auto=format&fit=crop&q=80&w=500',
                        category: v.category,
                        rating: v.rating || 4.5,
                        reviews: Math.floor(Math.random() * 200) + 10,
                        vendorCity: v.city,
                        vendorType: v.vendorType
                    }));
                setProducts(productList);

                // Map Vendors with vendorType 'rental' to the rentals tab
                const rentalList = initialVendors
                    .filter((v: any) => v.vendorType === 'rental')
                    .map((v: any) => ({
                        id: v._id,
                        vendorId: v._id,
                        vendorName: v.name,
                        name: v.name,
                        price: v.rentalDetails?.pricePerDay || 5000,
                        deposit: v.rentalDetails?.securityDeposit || 10000,
                        availableSizes: v.rentalDetails?.availableSizes || [],
                        gender: v.rentalDetails?.gender || 'unisex',
                        rentalCategory: v.rentalDetails?.rentalCategory || 'dress',
                        image: v.images?.[0] || 'https://images.unsplash.com/photo-1595777457583-95e059d581b8?auto=format&fit=crop&q=80&w=500',
                        category: v.category,
                        rating: v.rating || 4.5,
                        reviews: Math.floor(Math.random() * 200) + 10,
                        vendorCity: v.city || 'All India',
                        vendorType: v.vendorType,
                        description: v.description
                    }));
                setRentals(rentalList);

                // If logged in, also fetch the current shortlist from backend to sync state
                if (accessToken) {
                    const shortlistRes = await fetchWithTimeout(`${API_BASE_URL}/api/market-vendors/shortlist`, fetchOpts, 20000);
                    if (shortlistRes.ok) {
                        const shortlistData = await shortlistRes.json();
                        const backendIds = (shortlistData.shortlists || [])
                            .map((s: any) => s.marketVendorId?._id || s.marketVendorId)
                            .filter(Boolean);
                        
                        // Merge with local storage if any (local storage takes priority for current session)
                        const localSaved = localStorage.getItem('shadigram_shortlist');
                        let localIds = [];
                        if (localSaved) {
                            try {
                                const parsed = JSON.parse(localSaved);
                                if (Array.isArray(parsed)) {
                                    localIds = parsed;
                                }
                            } catch (e) {
                                console.error('Failed to parse local shortlist in fetchData:', e);
                            }
                        }
                        const merged = Array.from(new Set([...backendIds, ...localIds]));
                        
                        setShortlistedItems(merged);
                        localStorage.setItem('shadigram_shortlist', JSON.stringify(merged));
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching marketplace data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Calculate category counts based on current active tab
    const getCategoryCount = (catId: string) => {
        if (activeTab === 'products') {
            const list = catId === 'all' ? products : products.filter(p => p.category === catId);
            return list.length;
        } else if (activeTab === 'rentals') {
            const list = catId === 'all' ? rentals : rentals.filter(r => r.category === catId);
            return list.length;
        } else {
            const list = catId === 'all' ? vendors : vendors.filter(v => v.category === catId);
            return list.length;
        }
    };

    const handleAddToCart = (e: React.MouseEvent) => {
        e.stopPropagation();
        setCartCount(prev => prev + 1);
        // Toast could be added here
    };

    const filteredProducts = products.filter(p => {
        const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = selectedCategory === 'all' || (p.category || '').toLowerCase().trim() === selectedCategory.toLowerCase().trim();
        const matchesCity = selectedCity === 'All India' || (p.vendorCity || '').toLowerCase().trim() === selectedCity.toLowerCase().trim();
        return matchesSearch && matchesCategory && matchesCity;
    });

    const filteredRentals = rentals.filter(r => {
        const matchesSearch = r.name.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = selectedCategory === 'all' || (r.category || '').toLowerCase().trim() === selectedCategory.toLowerCase().trim();
        const matchesCity = selectedCity === 'All India' || (r.vendorCity || '').toLowerCase().trim() === selectedCity.toLowerCase().trim();
        return matchesSearch && matchesCategory && matchesCity;
    });

    const filteredVendors = vendors.filter(v => {
        const name = v.name || '';
        const category = v.category || '';
        const city = v.city || 'All India';

        const matchesSearch = name.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesCategory = selectedCategory === 'all' || (category || '').toLowerCase().trim() === selectedCategory.toLowerCase().trim();
        const matchesCity = selectedCity === 'All India' || (city || '').toLowerCase().trim() === selectedCity.toLowerCase().trim();
        
        return matchesSearch && matchesCategory && matchesCity;
    });

    // Derive shortlisted categories for the progress sidebar
    const shortlistedCategories = Array.from(new Set(
        shortlistedItems.map(id => {
            const product = products.find(p => p.id === id);
            if (product) return product.category;
            const rental = rentals.find(r => r.id === id);
            if (rental) return rental.category;
            const vendor = vendors.find(v => v._id === id);
            if (vendor) return vendor.category;
            return null;
        }).filter(Boolean) as string[]
    ));

    if (loading) return <div className="loading-state">Loading Shadi Gram Marketplace...</div>;

    return (
        <div className="marketplace-app">
            {/* E-commerce Header */}
            <header className={`market-header ${!showHeader ? 'header-hidden' : ''}`}>
                <div className="market-header-inner">
                    <div className="market-brand">
                        <h1>Shadi <span className="market-brand-highlight">Gram</span> Shop</h1>
                    </div>

                    <div className="market-search-location-widget">
                        <div className="market-search-input-wrapper">
                            <span className="search-icon"><Search size={18} /></span>
                            <input
                                type="text"
                                placeholder="Search lehengas, decor..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        
                        <div className="widget-divider"></div>

                        <div className="market-location-selector">
                            <button className="location-btn" onClick={() => setShowCityDropdown(!showCityDropdown)}>
                                <MapPin size={16} className="pin-icon" />
                                <span className="current-city">{selectedCity}</span>
                            </button>
                            
                            {showCityDropdown && (
                                <div className="city-dropdown glass">
                                    <button className="detect-location-btn" onClick={handleDetectLocation} disabled={isDetecting}>
                                        <Navigation size={14} className={isDetecting ? 'spinning' : ''} />
                                        {isDetecting ? 'Detecting...' : 'Detect My Location'}
                                    </button>
                                    <div className="city-list">
                                        {cities.map(city => (
                                            <button 
                                                key={city} 
                                                className={`city-item ${selectedCity === city ? 'active' : ''}`}
                                                onClick={() => {
                                                    setSelectedCity(city);
                                                    setShowCityDropdown(false);
                                                }}
                                            >
                                                {city}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="market-actions">
                        <button 
                            className="market-header-action-btn my-wedding-btn" 
                            onClick={() => setIsRoleModalOpen(true)}
                            title="My Wedding"
                        >
                           <Sparkles size={14} />
                           <span>My Wedding</span>
                        </button>
                        <button 
                            className="market-header-action-btn invite-btn" 
                            onClick={() => {
                                if (isInviteUser) {
                                    history.push('/invite/dashboard');
                                } else {
                                    setIsAuthModalOpen(true);
                                }
                            }}
                            title="e-Invitation"
                        >
                           <Mail size={14} />
                           <span>e-Invitation</span>
                        </button>
                        <button 
                            className="market-header-action-btn guest-btn" 
                            onClick={() => history.push('/be-a-guest')}
                            title="Be a Guest"
                        >
                           <Users size={14} />
                           <span>Be a Guest</span>
                        </button>
                        <button 
                            className="market-header-action-btn consult-btn" 
                            onClick={() => setIsAgencyEnquiryModalOpen(true)}
                            title="Consult an Expert"
                        >
                           <Building size={14} />
                           <span>Consult an Expert</span>
                        </button>


                        <div className="market-actions-divider"></div>

                        <button className="market-icon-btn" onClick={() => setIsRoleModalOpen(true)} title="Shortlist">
                            <Heart size={22} />
                            <span className="icon-badge">{shortlistedItems.length}</span>
                        </button>
                        <button className="market-icon-btn cart-btn" onClick={() => history.push('/cart')} title="Cart">
                            <ShoppingBag size={22} />
                            <span className="icon-badge">{shortlistedItems.length}</span>
                        </button>
                    </div>

                </div>
            </header>

            <RoleSelectionModal
                isOpen={isRoleModalOpen}
                onClose={() => setIsRoleModalOpen(false)}
                shortlistedCount={shortlistedItems.length}
            />
            <InviteAuthModal
                isOpen={isAuthModalOpen}
                onClose={() => setIsAuthModalOpen(false)}
            />

            <AgencyEnquiryModal
                isOpen={isAgencyEnquiryModalOpen}
                onClose={() => setIsAgencyEnquiryModalOpen(false)}
            />

            {/* Dynamic Carousel Banner Section */}
            <div 
                className="market-banner carousel-banner" 
                style={{ 
                    backgroundImage: `url(${BANNER_SLIDES[activeSlide].bgImage})`,
                    transition: 'background-image 0.8s ease-in-out'
                }}
            >
                <div 
                    className="carousel-overlay" 
                    style={{ 
                        position: 'absolute',
                        inset: 0,
                        background: BANNER_SLIDES[activeSlide].overlayColor,
                        transition: 'background 0.8s ease-in-out',
                        zIndex: 1
                    }}
                />

                <div className="banner-content" style={{ zIndex: 2 }}>
                    <span className="banner-tag animate-fadeInFast">{BANNER_SLIDES[activeSlide].tag}</span>
                    <h2 className="animate-slideUp">{BANNER_SLIDES[activeSlide].title}</h2>
                    <p className="animate-slideUpDelay">{BANNER_SLIDES[activeSlide].description}</p>
                    <button 
                        onClick={() => {
                            const slide = BANNER_SLIDES[activeSlide];
                            if (slide.action === 'agency') {
                                setIsAgencyEnquiryModalOpen(true);
                            } else if (slide.action === 'culturalExperience') {
                                alert("Experience traditional Indian hospitality! Please fill out the concierge request form to register as a cultural guest.");
                                setIsAgencyEnquiryModalOpen(true);
                            } else if (slide.action === 'categories') {
                                setSelectedCategory('all');
                                const target = document.querySelector('.market-layout-container');
                                if (target) target.scrollIntoView({ behavior: 'smooth' });
                            } else {
                                setActiveTab('products');
                            }
                        }}
                        className="banner-btn hover-glow"
                    >
                        {BANNER_SLIDES[activeSlide].btnText}
                    </button>
                </div>

                {/* Carousel Controls */}
                <button 
                    onClick={() => setActiveSlide(prev => (prev - 1 + BANNER_SLIDES.length) % BANNER_SLIDES.length)}
                    className="carousel-arrow left"
                    aria-label="Previous Slide"
                >
                    <ChevronLeft size={20} />
                </button>
                <button 
                    onClick={() => setActiveSlide(prev => (prev + 1) % BANNER_SLIDES.length)}
                    className="carousel-arrow right"
                    aria-label="Next Slide"
                >
                    <ChevronRight size={20} />
                </button>

                {/* Carousel Indicators */}
                <div className="carousel-dots">
                    {BANNER_SLIDES.map((_, idx) => (
                        <span 
                            key={idx} 
                            onClick={() => setActiveSlide(idx)}
                            className={`carousel-dot ${activeSlide === idx ? 'active' : ''}`}
                        />
                    ))}
                </div>
            </div>

            {/* Planning Progress Header Strip */}
            <div className="planning-progress-top-strip glass" style={{ margin: '20px auto 30px auto', maxWidth: '1200px', width: '90%', padding: '16px 24px', borderRadius: '12px', background: 'rgba(255, 255, 255, 0.02)', border: '1px solid rgba(212,175,55,0.15)', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Sparkles size={20} color="#d4af37" style={{ display: 'inline-flex' }} />
                        <h4 style={{ margin: 0, color: '#d4af37', fontSize: '1.1rem', fontWeight: 'bold', letterSpacing: '0.5px' }}>Wedding Planning Progress</h4>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px', fontSize: '0.9rem', color: '#ccc' }}>
                        <span>Essential Categories Found: <strong>{shortlistedCategories.length} / 7</strong></span>
                        <span style={{ background: 'rgba(212,175,55,0.2)', color: '#d4af37', padding: '2px 10px', borderRadius: '20px', fontWeight: 'bold', fontSize: '0.8rem' }}>
                            {Math.round((shortlistedCategories.length / 7) * 100)}% Complete
                        </span>
                    </div>
                </div>
                <div style={{ width: '100%', height: '8px', background: 'rgba(255,255,255,0.1)', borderRadius: '10px', overflow: 'hidden' }}>
                    <div style={{ width: `${(shortlistedCategories.length / 7) * 100}%`, height: '100%', background: 'linear-gradient(90deg, #d4af37, #f3e5ab)', borderRadius: '10px', transition: 'width 0.4s ease' }} />
                </div>
                <p style={{ margin: 0, fontSize: '0.8rem', color: '#aaa', fontStyle: 'italic' }}>
                    {shortlistedCategories.length === 7 
                        ? "Perfect! You've found and shortlisted premium vendors across all essential wedding categories."
                        : `Next Step: Explore top-rated ${['Venue Selection', 'Food & Catering', 'Decoration & Theme', 'Photography/Film', 'Bridal & Groom Wear', 'Makeup & Styling', 'DJ & Music'].find((_, i) => !shortlistedCategories.includes(['Venues', 'Catering', 'Decoration', 'Photography', 'Clothing', 'Beautician', 'DJ & Music'][i])) || 'essential'} vendors to perfect your dream wedding team.`}
                </p>
            </div>

            {/* Tabs for Products vs Boutiques */}
            <div className="market-tabs">
                <button
                    className={`market-tab ${activeTab === 'products' ? 'active' : ''}`}
                    onClick={() => setActiveTab('products')}
                >
                    Discover Products
                </button>
                <button
                    className={`market-tab ${activeTab === 'stores' ? 'active' : ''}`}
                    onClick={() => setActiveTab('stores')}
                >
                    Shadigram Recommendation
                </button>
                <button
                    className={`market-tab ${activeTab === 'rentals' ? 'active' : ''}`}
                    onClick={() => setActiveTab('rentals')}
                >
                    Dress & Jewelry Rentals 👗✨
                </button>
            </div>

            {/* Main Content Area */}
            <div className="market-layout-container" style={{ display: 'flex', gap: '24px', maxWidth: '1200px', width: '90%', margin: '0 auto 50px auto', alignItems: 'flex-start' }}>
                
                {/* Sidebar Mobile Backdrop Overlay */}
                {isMobileSidebarOpen && (
                    <div 
                        className="sidebar-backdrop" 
                        onClick={() => setIsMobileSidebarOpen(false)}
                        style={{
                            position: 'fixed',
                            inset: 0,
                            background: 'rgba(0,0,0,0.5)',
                            backdropFilter: 'blur(4px)',
                            zIndex: 1005
                        }}
                    />
                )}

                {/* Collapsible Categories Vertical Sidebar */}
                <div 
                    className={`categories-sidebar glass ${isSidebarCollapsed ? 'collapsed' : ''} ${isMobileSidebarOpen ? 'open' : ''}`} 
                    style={{ 
                        width: isSidebarCollapsed ? '70px' : '260px', 
                        minWidth: isSidebarCollapsed ? '70px' : '260px',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)', 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: '15px', 
                        padding: '20px 15px', 
                        borderRadius: '12px',
                        background: 'rgba(255, 255, 255, 0.02)',
                        border: '1px solid rgba(212,175,55,0.15)',
                        position: 'sticky',
                        top: showHeader ? 'calc(var(--app-bar-height) + 90px)' : 'calc(var(--app-bar-height) + 10px)',
                        zIndex: 90
                    }}
                >
                    <div style={{ display: 'flex', justifyContent: isSidebarCollapsed ? 'center' : 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(212,175,55,0.1)', paddingBottom: '12px', marginBottom: '8px' }}>
                        {!isSidebarCollapsed && <h4 style={{ margin: 0, color: '#d4af37', fontSize: '1.05rem', fontWeight: 'bold' }}>Categories</h4>}
                        <button 
                            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                            className="desktop-only"
                            style={{ background: 'transparent', border: 'none', color: '#d4af37', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4px' }}
                            title={isSidebarCollapsed ? "Expand Categories" : "Collapse Sidebar"}
                        >
                            {isSidebarCollapsed ? <Filter size={18} /> : <ChevronLeft size={18} />}
                        </button>
                        
                        {/* Mobile Close Button */}
                        <button 
                            onClick={() => setIsMobileSidebarOpen(false)}
                            className="mobile-only"
                            style={{ background: 'transparent', border: 'none', color: '#d4af37', cursor: 'pointer', display: 'none', alignItems: 'center', justifyContent: 'center', padding: '4px' }}
                        >
                            <X size={18} />
                        </button>
                    </div>

                    <div 
                        className="sidebar-cat-list"
                        style={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: '8px',
                            overflowY: 'auto',
                            maxHeight: 'calc(100vh - var(--app-bar-height) - 195px)',
                            paddingRight: '4px'
                        }}
                    >
                        {categories.map(cat => {
                            const count = getCategoryCount(cat.id);
                            const isActive = selectedCategory === cat.id;
                            return (
                                <button
                                    key={cat.id}
                                    className={`sidebar-cat-btn ${isActive ? 'active' : ''}`}
                                    onClick={() => {
                                        setSelectedCategory(cat.id);
                                        setIsMobileSidebarOpen(false);
                                    }}
                                    title={isSidebarCollapsed ? `${cat.name} (${count})` : ''}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: isSidebarCollapsed ? 'center' : 'flex-start',
                                        gap: '12px',
                                        padding: '12px',
                                        border: isActive ? '1px solid #d4af37' : '1px solid transparent',
                                        borderRadius: '8px',
                                        background: isActive ? 'rgba(212,175,55,0.1)' : 'transparent',
                                        color: isActive ? '#d4af37' : '#fff',
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                        transition: 'all 0.2s ease',
                                        width: '100%'
                                    }}
                                >
                                    <span style={{ fontSize: '1.2rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{cat.icon}</span>
                                    {!isSidebarCollapsed && (
                                        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                                            <span style={{ fontSize: '0.9rem', fontWeight: isActive ? 'bold' : 'normal' }}>{cat.name}</span>
                                            {count > 0 && <span style={{ fontSize: '0.75rem', background: isActive ? 'rgba(212,175,55,0.2)' : 'rgba(255,255,255,0.1)', padding: '2px 6px', borderRadius: '10px', color: isActive ? '#d4af37' : '#bbb' }}>{count}</span>}
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <div className="market-main-content" style={{ flex: 1 }}>
                    {activeTab === 'products' ? (
                        <>
                            <div className="section-title-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', background: '#ffffff', padding: '12px 24px', borderRadius: '12px', border: '1px solid #E2E8F0', boxShadow: '0 4px 15px rgba(33, 150, 243, 0.03)', marginBottom: '2rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                                    <h3 style={{ margin: 0, fontFamily: "'Playfair Display', serif", fontSize: '1.4rem', fontWeight: 600, color: '#1A202C' }}>Trending Now</h3>
                                    <span className="items-count" style={{ fontSize: '0.8rem', padding: '2px 8px', borderRadius: '12px', background: 'rgba(33, 150, 243, 0.1)', color: '#2196F3', fontWeight: 600 }}>{filteredProducts.length} Items</span>
                                </div>
                                
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
                                    <button 
                                        className="mobile-category-trigger"
                                        onClick={() => setIsMobileSidebarOpen(true)}
                                        style={{ margin: 0 }}
                                    >
                                        <Filter size={16} style={{ marginRight: '6px' }} /> Categories
                                    </button>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                                        <div className="price-filter" style={{ borderRight: '1px solid #E2E8F0', paddingRight: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                                            <label style={{ fontSize: '0.85rem', color: '#4A5568', fontWeight: 500, whiteSpace: 'nowrap' }}>Max: ₹{maxPrice.toLocaleString('en-IN')}</label>
                                            <input type="range" min="5000" max="2000000" step="10000" value={maxPrice} onChange={(e) => setMaxPrice(Number(e.target.value))} style={{ width: '120px', accentColor: '#FF4081', cursor: 'pointer' }} />
                                        </div>
                                        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="sort-select" style={{ border: 'none', background: 'transparent', outline: 'none', fontWeight: 500, cursor: 'pointer', fontSize: '0.95rem', color: '#2D3748' }}>
                                            <option value="recommended">Sort: Recommended</option>
                                            <option value="price-low">Price: Low to High</option>
                                            <option value="price-high">Price: High to Low</option>
                                            <option value="rating">Top Rated</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="products-grid">
                                {filteredProducts.length > 0 ? filteredProducts.map(product => (
                                    <div key={product.id} className="product-card" onClick={() => history.push(`/vendor/${product.vendorId}`)} style={{ cursor: 'pointer' }}>
                                        <div className="product-img-wrapper">
                                            <img src={product.image} alt={product.name} />
                                            <button
                                                className={`wishlist-btn ${shortlistedItems.includes(product.id) ? 'active' : ''}`}
                                                onClick={(e) => toggleShortlist(product.id, e)}
                                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0' }}
                                            >
                                                <Heart 
                                                    size={18} 
                                                    fill={shortlistedItems.includes(product.id) ? "#FF4081" : "none"} 
                                                    color={shortlistedItems.includes(product.id) ? "#FF4081" : "#A0AEC0"} 
                                                />
                                            </button>
                                        </div>
                                        <div className="product-info">
                                            <div className="product-vendor-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div className="product-vendor">{product.vendorName}</div>
                                                {user?.role === 'agency' && (
                                                    <button 
                                                        className={`agency-star-btn ${vendors.find(v => v._id === product.vendorId)?.isPreferred ? 'active' : ''}`}
                                                        onClick={(e) => togglePreference(product.vendorId, e)}
                                                        title="Mark as Agency Preferred"
                                                        style={{ 
                                                            background: 'none', 
                                                            border: 'none', 
                                                            cursor: 'pointer', 
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            padding: '0'
                                                        }}
                                                    >
                                                        <Star 
                                                            size={18} 
                                                            fill={vendors.find(v => v._id === product.vendorId)?.isPreferred ? "#FFD700" : "none"} 
                                                            color={vendors.find(v => v._id === product.vendorId)?.isPreferred ? "#FFD700" : "#ccc"} 
                                                        />
                                                    </button>
                                                )}
                                            </div>
                                            <h4 className="product-name">{product.name}</h4>
                                            <div className="product-rating" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <span className="stars" style={{ display: 'inline-flex', gap: '2px' }}>
                                                    {Array.from({ length: 5 }).map((_, i) => (
                                                        <Star 
                                                            key={i} 
                                                            size={14} 
                                                            fill={i < Math.round(product.rating) ? "#FF4081" : "none"} 
                                                            color={i < Math.round(product.rating) ? "#FF4081" : "#E2E8F0"} 
                                                        />
                                                    ))}
                                                </span>
                                                <span className="reviews">({product.reviews})</span>
                                            </div>
                                            <div className="product-price-row">
                                                <div className="price-block">
                                                    <span className="current-price">₹{product.price.toLocaleString('en-IN')}</span>
                                                    {product.originalPrice && (
                                                        <span className="original-price">₹{product.originalPrice.toLocaleString('en-IN')}</span>
                                                    )}
                                                </div>
                                                <button 
                                                    className={`market-action-icon-btn ${product.vendorType === 'product' ? 'product-type' : 'service-type'}`} 
                                                    onClick={(e) => {
                                                        handleAddToCart(e);
                                                        if (!shortlistedItems.includes(product.id)) {
                                                            toggleShortlist(product.id, e);
                                                        }
                                                        setAddedId(product.id);
                                                        setTimeout(() => setAddedId(null), 1500);
                                                    }}
                                                    title={product.vendorType === 'product' ? 'Add to Bag' : 'Add to Booking'}
                                                >
                                                    {addedId === product.id ? <Check size={20} /> : (
                                                        product.vendorType === 'product' ? <Plus size={20} /> : <CalendarPlus size={20} />
                                                    )}
                                                </button>

                                                {product.vendorType === 'service' && (
                                                    <button 
                                                        className="market-action-icon-btn avail-type" 
                                                        onClick={(e) => handleCheckAvailability(product.vendorId, product.vendorName, e)}
                                                        title="Check Availability"
                                                    >
                                                        <Clock size={20} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )) : (
                                    <div className="empty-state">
                                        <div className="empty-icon">
                                            <MapPin size={48} style={{ color: '#A0AEC0' }} />
                                        </div>
                                        <h4>No items found in {selectedCity}</h4>
                                        <p>Try searching in "All India" to see more options for your wedding.</p>
                                        <button className="clear-city-btn" onClick={() => setSelectedCity('All India')}>
                                            Show All India
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : activeTab === 'rentals' ? (
                        <>
                            <div className="section-title-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', background: '#ffffff', padding: '12px 24px', borderRadius: '12px', border: '1px solid #E2E8F0', boxShadow: '0 4px 15px rgba(197, 160, 89, 0.05)', marginBottom: '2rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                                    <h3 style={{ margin: 0, fontFamily: "'Playfair Display', serif", fontSize: '1.4rem', fontWeight: 600, color: '#3E1F24' }}>Luxury Dress & Jewelry Rentals</h3>
                                    <span className="items-count" style={{ fontSize: '0.8rem', padding: '2px 8px', borderRadius: '12px', background: 'rgba(197, 160, 89, 0.15)', color: '#c5a059', fontWeight: 600 }}>{filteredRentals.length} Items</span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
                                    <button 
                                        className="mobile-category-trigger"
                                        onClick={() => setIsMobileSidebarOpen(true)}
                                        style={{ margin: 0 }}
                                    >
                                        <Filter size={16} style={{ marginRight: '6px' }} /> Categories
                                    </button>
                                    <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="sort-select" style={{ border: 'none', background: 'transparent', outline: 'none', fontWeight: 500, cursor: 'pointer', fontSize: '0.95rem', color: '#2D3748' }}>
                                        <option value="recommended">Sort: Recommended</option>
                                        <option value="price-low">Price: Low to High</option>
                                        <option value="price-high">Price: High to Low</option>
                                        <option value="rating">Top Rated</option>
                                    </select>
                                </div>
                            </div>

                            <div className="products-grid">
                                {filteredRentals.length > 0 ? filteredRentals.map(rental => (
                                    <div 
                                        key={rental.id} 
                                        className="product-card" 
                                        onClick={() => {
                                            setSelectedRentalForDetails(rental);
                                            setRentalStartDate('');
                                            setRentalEndDate('');
                                            setSelectedSize('');
                                        }}
                                        style={{ cursor: 'pointer', border: '1px solid rgba(197, 160, 89, 0.15)', boxShadow: '0 4px 20px rgba(197, 160, 89, 0.03)' }}
                                    >
                                        <div className="product-img-wrapper">
                                            <img src={rental.image} alt={rental.name} />
                                            <div className="rental-badge" style={{ position: 'absolute', top: '10px', left: '10px', background: '#3e1f24', color: '#c5a059', fontSize: '0.75rem', fontWeight: 'bold', padding: '4px 10px', borderRadius: '20px', border: '1px solid #c5a059' }}>
                                                FOR RENT
                                            </div>
                                            <button
                                                className={`wishlist-btn ${shortlistedItems.includes(rental.id) ? 'active' : ''}`}
                                                onClick={(e) => toggleShortlist(rental.id, e)}
                                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0' }}
                                            >
                                                <Heart 
                                                    size={18} 
                                                    fill={shortlistedItems.includes(rental.id) ? "#FF4081" : "none"} 
                                                    color={shortlistedItems.includes(rental.id) ? "#FF4081" : "#A0AEC0"} 
                                                />
                                            </button>
                                        </div>
                                        <div className="product-info" style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                            <div className="product-vendor-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <div className="product-vendor">{rental.vendorName}</div>
                                            </div>
                                            <h4 className="product-name" style={{ fontFamily: "'Playfair Display', serif", color: '#3E1F24', fontSize: '1.05rem', margin: '2px 0' }}>{rental.name}</h4>
                                            <div className="product-rating" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <span className="stars" style={{ display: 'inline-flex', gap: '2px' }}>
                                                    {Array.from({ length: 5 }).map((_, i) => (
                                                        <Star 
                                                            key={i} 
                                                            size={14} 
                                                            fill={i < Math.round(rental.rating) ? "#c5a059" : "none"} 
                                                            color={i < Math.round(rental.rating) ? "#c5a059" : "#E2E8F0"} 
                                                        />
                                                    ))}
                                                </span>
                                                <span className="reviews">({rental.reviews})</span>
                                            </div>
                                            <div className="product-price-row" style={{ marginTop: '5px', paddingTop: '8px', borderTop: '1px solid rgba(0,0,0,0.04)' }}>
                                                <div className="price-block" style={{ display: 'flex', flexDirection: 'column' }}>
                                                    <span className="current-price" style={{ color: '#c5a059', fontSize: '1.15rem' }}>₹{rental.price.toLocaleString('en-IN')}<span style={{ fontSize: '0.8rem', fontWeight: 'normal', color: '#718096' }}>/day</span></span>
                                                    <span style={{ fontSize: '0.75rem', color: '#718096' }}>Deposit: ₹{rental.deposit.toLocaleString('en-IN')}</span>
                                                </div>
                                                <button 
                                                    className="market-action-icon-btn product-type" 
                                                    style={{ background: 'linear-gradient(135deg, #3e1f24 0%, #c5a059 100%)' }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedRentalForDetails(rental);
                                                        setRentalStartDate('');
                                                        setRentalEndDate('');
                                                        setSelectedSize('');
                                                    }}
                                                    title="Configure Rental"
                                                >
                                                    <Plus size={20} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )) : (
                                    <div className="empty-state">
                                        <div className="empty-icon">
                                            <Shirt size={48} style={{ color: '#c5a059' }} />
                                        </div>
                                        <h4>No rentals found in this category</h4>
                                        <p>Try clearing your category filter to see our premium wedding dresses and jewelry collections.</p>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="section-title-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', background: '#ffffff', padding: '12px 24px', borderRadius: '12px', border: '1px solid #E2E8F0', boxShadow: '0 4px 15px rgba(33, 150, 243, 0.03)', marginBottom: '2rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                                    <h3 style={{ margin: 0, fontFamily: "'Playfair Display', serif", fontSize: '1.4rem', fontWeight: 600, color: '#1A202C' }}>✨ Shadigram Choice</h3>
                                </div>
                                
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
                                    <button 
                                        className="mobile-category-trigger"
                                        onClick={() => setIsMobileSidebarOpen(true)}
                                        style={{ margin: 0 }}
                                    >
                                        <Filter size={16} style={{ marginRight: '6px' }} /> Categories
                                    </button>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="sort-select" style={{ border: 'none', background: 'transparent', outline: 'none', fontWeight: 500, cursor: 'pointer', fontSize: '0.95rem', color: '#2D3748' }}>
                                            <option value="recommended">Sort: Recommended</option>
                                            <option value="rating">Top Rated</option>
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="stores-grid">
                                {filteredVendors.length > 0 ? filteredVendors.map(vendor => (
                                    <div key={vendor._id} className="store-card" onClick={() => history.push(`/vendor/${vendor._id}`)}>
                                        <div className="store-cover" style={{ backgroundImage: `url(${vendor.images?.[0] || 'https://images.unsplash.com/photo-1519225421980-715cb0215aed?auto=format&fit=crop&q=80'})` }}>
                                            <div className="store-category-badge">{vendor.category}</div>
                                        </div>
                                        <div className="store-info">
                                            <div className="store-header">
                                                <h4 className="store-name">{vendor.name}</h4>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                    {user?.role === 'agency' && (
                                                        <button 
                                                            className={`agency-star-btn ${vendor.isPreferred ? 'active' : ''}`}
                                                            onClick={(e) => togglePreference(vendor._id, e)}
                                                            title={vendor.isPreferred ? 'Remove from Preferred' : 'Mark as Agency Preferred'}
                                                            style={{ 
                                                                background: 'none', 
                                                                border: 'none', 
                                                                cursor: 'pointer', 
                                                                padding: '0',
                                                                display: 'flex',
                                                                alignItems: 'center'
                                                            }}
                                                        >
                                                            <Star 
                                                                size={20} 
                                                                fill={vendor.isPreferred ? "#FFD700" : "none"} 
                                                                color={vendor.isPreferred ? "#FFD700" : "#ccc"} 
                                                            />
                                                        </button>
                                                    )}
                                                    <span className="store-rating" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                        <Star size={14} fill="#FF4081" color="#FF4081" />
                                                        {(vendor.rating ?? 4.5).toFixed(1)}
                                                    </span>
                                                </div>
                                            </div>
                                            <p className="store-location">📍 {vendor.city || 'Available Nationwide'}</p>
                                            <p className="store-desc">{vendor.description?.substring(0, 80) || 'Premium wedding services and luxury experiences.'}...</p>
                                            <div className="store-actions-row" style={{ display: 'flex', gap: '10px', marginTop: 'auto', paddingTop: '1rem' }}>
                                                <button 
                                                    className={`market-action-icon-btn ${vendor.vendorType === 'product' ? 'product-type' : 'service-type'}`} 
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (!shortlistedItems.includes(vendor._id)) {
                                                        toggleShortlist(vendor._id, e);
                                                    }
                                                    setAddedId(vendor._id);
                                                    setTimeout(() => setAddedId(null), 1500);
                                                }}
                                                title={vendor.vendorType === 'product' ? 'Add to Bag' : 'Add to Booking'}
                                            >
                                                {addedId === vendor._id ? <Check size={20} /> : (
                                                    vendor.vendorType === 'product' ? <Plus size={20} /> : <CalendarPlus size={20} />
                                                )}
                                                </button>

                                                {vendor.vendorType === 'service' && (
                                                    <button 
                                                        className="market-action-icon-btn avail-type" 
                                                        onClick={(e) => handleCheckAvailability(vendor._id, vendor.name, e)}
                                                        title="Check Availability"
                                                    >
                                                        <Clock size={20} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )) : (
                                    <div className="empty-state">
                                        <div className="empty-icon">
                                            <Building size={48} style={{ color: '#A0AEC0' }} />
                                        </div>
                                        <h4>No verified partners in {selectedCity}</h4>
                                        <p>Expand your search to "All India" to discover the best wedding experts.</p>
                                        <button className="clear-city-btn" onClick={() => setSelectedCity('All India')}>
                                            Show All India
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
            <CheckAvailabilityModal 
                isOpen={isAvailModalOpen}
                onClose={() => setIsAvailModalOpen(false)}
                vendorNames={selectedVendorForAvail.map(v => v.name)}
                vendorIds={selectedVendorForAvail.map(v => v.id)}
                onSubmit={submitEnquiry}
            />

            {selectedRentalForDetails && (
                <div className="invite-modal-overlay" onClick={() => setSelectedRentalForDetails(null)}>
                    <div className="invite-modal-card glass" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px', padding: '2.5rem 2rem' }}>
                        <button className="invite-modal-close" onClick={() => setSelectedRentalForDetails(null)}>
                            <X size={18} />
                        </button>
                        
                        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                            <span className="gold-badge" style={{ fontSize: '0.8rem', padding: '4px 12px', borderRadius: '20px', background: 'linear-gradient(135deg, #c5a059 0%, #edd39e 100%)', color: '#3e1f24', fontWeight: 'bold' }}>
                                DRESS & JEWELRY RENTALS
                            </span>
                            <h2 style={{ fontFamily: "'Playfair Display', serif", color: '#3E1F24', fontSize: '1.6rem', marginTop: '0.5rem' }}>
                                {selectedRentalForDetails.name}
                            </h2>
                            <p style={{ color: '#718096', fontSize: '0.9rem' }}>By {selectedRentalForDetails.vendorName}</p>
                        </div>

                        <div style={{ display: 'flex', gap: '15px', marginBottom: '1.5rem' }}>
                            <img 
                                src={selectedRentalForDetails.image} 
                                alt={selectedRentalForDetails.name} 
                                style={{ width: '120px', height: '150px', objectFit: 'cover', borderRadius: '12px', border: '1px solid rgba(197,160,89,0.3)' }} 
                            />
                            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <p style={{ fontSize: '0.9rem', color: '#4A5568', margin: 0 }}>{selectedRentalForDetails.description || 'Premium rental designer item for wedding.'}</p>
                                <div style={{ fontSize: '0.85rem', color: '#718096' }}>📍 {selectedRentalForDetails.vendorCity}</div>
                                <div style={{ fontSize: '1rem', fontWeight: 'bold', color: '#c5a059' }}>
                                    ₹{selectedRentalForDetails.price.toLocaleString('en-IN')}/day
                                </div>
                                <div style={{ fontSize: '0.85rem', color: '#475569' }}>
                                    Refundable Deposit: <strong>₹{selectedRentalForDetails.deposit.toLocaleString('en-IN')}</strong>
                                </div>
                            </div>
                        </div>

                        <form onSubmit={(e) => {
                            e.preventDefault();
                            if (!rentalStartDate || !rentalEndDate) {
                                alert('Please select rental dates');
                                return;
                            }
                            if (selectedRentalForDetails.rentalCategory === 'dress' && !selectedSize) {
                                alert('Please select a size');
                                return;
                            }

                            // Save selections in localStorage
                            const saved = localStorage.getItem('shadigram_rental_selections');
                            const selections = saved ? JSON.parse(saved) : {};
                            selections[selectedRentalForDetails.id] = {
                                startDate: rentalStartDate,
                                endDate: rentalEndDate,
                                size: selectedSize
                            };
                            localStorage.setItem('shadigram_rental_selections', JSON.stringify(selections));

                            // Add to shortlist (cart)
                            if (!shortlistedItems.includes(selectedRentalForDetails.id)) {
                                toggleShortlist(selectedRentalForDetails.id, e as any);
                            }

                            alert('Item added to cart with rental reservations!');
                            setSelectedRentalForDetails(null);
                        }} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            
                            {selectedRentalForDetails.rentalCategory === 'dress' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                    <label style={{ fontSize: '0.9rem', fontWeight: 'bold', color: '#475569' }}>Select Size *</label>
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        {['S', 'M', 'L', 'XL', 'XXL'].map(size => {
                                            const isAvailable = selectedRentalForDetails.availableSizes?.includes(size) ?? true;
                                            return (
                                                <button
                                                    key={size}
                                                    type="button"
                                                    disabled={!isAvailable}
                                                    onClick={() => setSelectedSize(size)}
                                                    style={{
                                                        flex: 1,
                                                        padding: '8px',
                                                        border: selectedSize === size ? '2px solid #c5a059' : '1px solid #cbd5e1',
                                                        borderRadius: '8px',
                                                        background: selectedSize === size ? 'rgba(197, 160, 89, 0.1)' : '#fff',
                                                        color: selectedSize === size ? '#c5a059' : (isAvailable ? '#1e293b' : '#cbd5e1'),
                                                        cursor: isAvailable ? 'pointer' : 'not-allowed',
                                                        fontWeight: 'bold',
                                                        opacity: isAvailable ? 1 : 0.5
                                                    }}
                                                >
                                                    {size}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: '15px' }}>
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                    <label style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#475569' }}>Start Date *</label>
                                    <input 
                                        type="date" 
                                        value={rentalStartDate} 
                                        onChange={(e) => setRentalStartDate(e.target.value)}
                                        min={new Date().toISOString().split('T')[0]}
                                        required 
                                        style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', outline: 'none', width: '100%', boxSizing: 'border-box' }}
                                    />
                                </div>
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                    <label style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#475569' }}>End Date *</label>
                                    <input 
                                        type="date" 
                                        value={rentalEndDate} 
                                        onChange={(e) => setRentalEndDate(e.target.value)}
                                        min={rentalStartDate || new Date().toISOString().split('T')[0]}
                                        required 
                                        style={{ padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: '8px', outline: 'none', width: '100%', boxSizing: 'border-box' }}
                                    />
                                </div>
                            </div>

                            {rentalStartDate && rentalEndDate && (
                                <div style={{ background: 'rgba(197, 160, 89, 0.05)', border: '1px dashed #c5a059', borderRadius: '10px', padding: '12px', fontSize: '0.9rem', color: '#3E1F24' }}>
                                    {(() => {
                                        const s = new Date(rentalStartDate);
                                        const e = new Date(rentalEndDate);
                                        const diff = e.getTime() - s.getTime();
                                        const days = Math.max(1, Math.ceil(diff / (1000 * 60 * 60 * 24)));
                                        const rentCost = selectedRentalForDetails.price * days;
                                        return (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <span>Rental Cost ({days} {days === 1 ? 'day' : 'days'}):</span>
                                                    <strong>₹{rentCost.toLocaleString('en-IN')}</strong>
                                                </div>
                                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                                    <span>Security Deposit (Refundable):</span>
                                                    <strong>₹{selectedRentalForDetails.deposit.toLocaleString('en-IN')}</strong>
                                                </div>
                                                <hr style={{ border: 'none', borderTop: '1px solid rgba(197,160,89,0.2)', margin: '4px 0' }} />
                                                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                                                    <span>Est. Total Payable:</span>
                                                    <span style={{ color: '#c5a059' }}>₹{(rentCost + selectedRentalForDetails.deposit).toLocaleString('en-IN')}</span>
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}

                            <button 
                                type="submit" 
                                className="invite-modal-submit-btn"
                                style={{ background: 'linear-gradient(135deg, #3e1f24 0%, #c5a059 100%)', color: '#fff', border: 'none', borderRadius: '10px', padding: '12px', fontWeight: 'bold', cursor: 'pointer', marginTop: '10px', width: '100%' }}
                            >
                                Confirm Rental Reservation 🛍️
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Marketplace;

